import * as Select from '@radix-ui/react-select';
import type { JSX, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { chevron } from '~/components/DropdownTreeSingleSelect/styles';
import type {
  InternalDropdownOption,
  OptionTemplateComponent,
  RadixUiProps,
} from '~/components/DropdownTreeSingleSelect/types';
import { Icon } from '~/components/Icon';
import { cn } from '~/lib/cn';

interface DropdownTriggerProps extends RadixUiProps {
  selectedOption?: InternalDropdownOption;
  isOpen: boolean;
  OptionTemplate?: OptionTemplateComponent;
}

export function DropdownTrigger({
  selectedOption,
  isOpen,
  OptionTemplate,
  radix = {},
}: DropdownTriggerProps) {
  const { t } = useTranslation('common');

  const placeholderText =
    radix.selectValue?.placeholder || t('dropdownTree.placeholder');
  let valueDisplay: JSX.Element | ReactNode = placeholderText;

  if (selectedOption) {
    if (OptionTemplate) {
      valueDisplay = <OptionTemplate option={selectedOption} />;
    } else {
      valueDisplay = (
        <span className="truncate">
          {selectedOption.label || selectedOption.value}
        </span>
      );
    }
  }

  return (
    <Select.Trigger
      {...radix.selectTrigger}
      className={cn(
        'relative flex min-h-2xl w-full cursor-pointer items-center justify-between gap-sm rounded-xs border py-sm pl-md pr-sm text-body-sm',
        'outline-none duration-300 bg-emphasis-default border-emphasis-subtle',
        'enabled:hover:bg-emphasis-subtle enabled:hover:border-emphasis-strong disabled:cursor-not-allowed disabled:border-none',
        radix.selectTrigger?.className,
      )}
      aria-expanded={isOpen}
    >
      <Select.Value
        {...radix.selectValue}
        placeholder={placeholderText}
        asChild
      >
        <div
          className={cn(
            {
              'text-subtle': !selectedOption?.value,
              'text-disabled': radix.select?.disabled,
            },
            'truncate',
            radix.selectValue?.className,
          )}
        >
          {valueDisplay}
        </div>
      </Select.Value>
      <Select.Icon
        {...radix.selectIcon}
        className={cn('text-default', radix.selectIcon?.className, {
          'text-disabled': radix.select?.disabled,
        })}
      >
        <Icon
          className={cn(
            chevron({
              state: isOpen ? 'open' : 'default',
            }),
            'mr-sm',
          )}
          size="md"
          type="chevron-down"
        />
      </Select.Icon>
    </Select.Trigger>
  );
}

DropdownTrigger.displayName = 'DropdownTrigger';
